// ============================================================
// ByOurConsent.com -- Constitutional Research Assistant
// /premium/constitutional-research
// Premium member tool -- Civic Intelligence Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const RESEARCH_TYPES = [
    { id: 'right_exist',   label: 'Does this right exist?',              desc: 'Find out if a specific right is protected and under what conditions.' },
    { id: 'what_means',    label: 'What does this provision mean?',       desc: 'Plain English interpretation of a specific amendment, article, or clause.' },
    { id: 'does_violate',  label: 'Does this action violate the constitution?', desc: 'Analyze whether a specific government action is constitutional.' },
    { id: 'what_process',  label: 'What process is required?',            desc: 'What steps must government follow before it can take a specific action?' },
    { id: 'fed_vs_state',  label: 'Federal vs state authority',           desc: 'Who has the power to do this — federal government, state, or neither?' },
    { id: 'historical',    label: 'Historical context and original intent',desc: 'What did this provision mean when written and how has it evolved?' },
];

const CONSTITUTION_SCOPE = [
    { id: 'federal',  label: 'Federal Constitution only' },
    { id: 'state',    label: 'State Constitution only' },
    { id: 'both',     label: 'Compare federal and state' },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

const btnPrimary = {
    background: '#8b1a1a', color: '#f5f0e8', border: 'none',
    padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif',
    letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer'
};

const btnSecondary = {
    background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa',
    padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif',
    borderRadius: '3px', cursor: 'pointer'
};

// ============================================================
// Main component
// ============================================================
const ConstitutionalResearchPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        scope:        'federal',
        state:        '',
        researchType: '',
        question:     '',
        context:      '',
        specificProvision: ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const selectedType = RESEARCH_TYPES.find(r => r.id === form.researchType);

    const canProceed = form.researchType && form.question.trim().length > 10 &&
        (form.scope === 'federal' || form.state);

    // ============================================================
    // Generate research via backend
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const scopeDesc = form.scope === 'federal'
            ? 'the United States Constitution'
            : form.scope === 'state'
                ? `the ${form.state} State Constitution`
                : `both the United States Constitution and the ${form.state} State Constitution`;

        const prompt = `You are an expert constitutional law scholar with deep knowledge of both federal and state constitutions, Supreme Court precedent, and constitutional history. Provide a thorough, accurate research response.

RESEARCH REQUEST:
- Type: ${selectedType?.label}
- Constitutional scope: ${scopeDesc}
- Question: ${form.question}
${form.specificProvision ? `- Specific provision referenced: ${form.specificProvision}` : ''}
${form.context ? `- Additional context: ${form.context}` : ''}

Provide a comprehensive research response with the following sections:

## DIRECT ANSWER
Answer the question clearly and directly in 2-3 sentences. No hedging — give the most accurate answer based on constitutional law.

## CONSTITUTIONAL BASIS
- Cite the specific article, amendment, section, or clause that applies
- Quote the relevant text of the provision
- Explain what it means in plain English
${form.scope !== 'federal' ? `- Include relevant ${form.state} state constitutional provisions where applicable` : ''}

## LEGAL INTERPRETATION
- How have courts interpreted this provision?
- Key Supreme Court cases that define its scope (cite case names and years)
- Current legal standard that applies
- Any ongoing legal debates or evolving interpretations

## PRACTICAL IMPLICATIONS
- What this means for ordinary citizens in practical terms
- What government can and cannot do under this provision
- Any exceptions or limitations that apply
- How to invoke this right or protection if needed

${form.scope === 'both' ? `## FEDERAL VS STATE COMPARISON
- How does the federal protection compare to ${form.state} state protection?
- Does the state constitution provide stronger or weaker protection?
- Which applies in different situations?` : ''}

## HISTORICAL CONTEXT
- Why was this provision included?
- Original intent of the framers
- How interpretation has evolved over time

## KEY TAKEAWAYS
3-5 bullet points summarizing the most important things to know.

Be precise, cite specific cases and constitutional provisions, and explain complex legal concepts in accessible language. If there are areas of genuine legal uncertainty, acknowledge them clearly.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({ research: text, question: form.question, scope: scopeDesc });
            setStep(3);
        } catch (err) {
            setError('Failed to generate research. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download
    // ============================================================
    const downloadWord = () => {
        downloadReportAsWord(result.research, `Constitutional-Research-${Date.now()}`);
    };

    const reset = () => {
        setStep(1); setResult(null); setError('');
        setForm({ scope: 'federal', state: '', researchType: '', question: '', context: '', specificProvision: '' });
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet><title>Constitutional Research Assistant — By Our Consent</title></Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Civic intelligence suite
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        Constitutional Research Assistant
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Research any constitutional question about federal or state constitutions.
                        Guided intake ensures you get a precise, well-cited answer — not a generic overview.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Research setup ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — What do you want to research?
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Research type — pick the closest match</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Choosing the right research type focuses the analysis. "Does this right exist?" gets you a yes/no
                                    with citations. "Does this action violate the constitution?" gets you a legal analysis of a specific
                                    government action. Pick the one that matches what you actually want to know.
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Your question — be specific, not general</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Vague questions get vague answers. The more specific your question, the more useful the response.
                                    Include who is doing what, to whom, and in what context.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '4px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#8b1a1a' }}>Too vague:</strong> "What are my free speech rights?"
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#3b6d11' }}>Much better:</strong> "Can my city council pass an ordinance banning political signs
                                    larger than 2 square feet on private residential property?"
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Specific provision — name it if you know it</div>
                                <div style={{ marginBottom: '6px' }}>
                                    If you already know which amendment or clause is involved, name it. This focuses the research
                                    on the most relevant legal framework instead of casting a wide net.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "First Amendment — Free Speech Clause" or
                                    "Article I Section 8 — Commerce Clause" or "14th Amendment Equal Protection Clause"
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Additional context — explain what prompted the question</div>
                                <div style={{ marginBottom: '6px' }}>
                                    A sentence about why you're asking helps tailor the answer to your actual situation.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "My HOA is threatening to fine me for displaying a political sign
                                    on my own property during election season. I want to know if they have the legal authority to do this
                                    under Virginia law and the First Amendment."
                                </div>
                            </div>
                        </details>

                        {/* Constitutional scope */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Which constitution?</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {CONSTITUTION_SCOPE.map(s => (
                                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: form.scope === s.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.scope === s.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1, fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.scope === s.id ? 600 : 400 }}>{s.label}</div>
                                        <input type="radio" name="scope" checked={form.scope === s.id}
                                            onChange={() => set('scope', s.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0 }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* State selector */}
                        {(form.scope === 'state' || form.scope === 'both') && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>State</label>
                                <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                    <option value="">Select state...</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Research type */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>What type of research?</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {RESEARCH_TYPES.map(r => (
                                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: form.researchType === r.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.researchType === r.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.researchType === r.id ? 600 : 400 }}>{r.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{r.desc}</div>
                                        </div>
                                        <input type="radio" name="researchType" checked={form.researchType === r.id}
                                            onChange={() => set('researchType', r.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0 }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Main question */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Your question *</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={4}
                                placeholder={
                                    form.researchType === 'right_exist' ? "e.g. Do I have a constitutional right to record police in public?" :
                                    form.researchType === 'what_means' ? "e.g. What does 'cruel and unusual punishment' actually mean under the 8th Amendment?" :
                                    form.researchType === 'does_violate' ? "e.g. Can a city council ban political signs on private property?" :
                                    form.researchType === 'what_process' ? "e.g. What process must the government follow before seizing private property?" :
                                    form.researchType === 'fed_vs_state' ? "e.g. Does my state have stronger free speech protections than the First Amendment?" :
                                    form.researchType === 'historical' ? "e.g. What did the founders intend by 'well regulated militia' in the Second Amendment?" :
                                    "Describe your constitutional question in plain language..."
                                }
                                value={form.question}
                                onChange={e => set('question', e.target.value)} />
                        </div>

                        {/* Specific provision */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Specific amendment, article, or clause (optional)</label>
                            <input style={iStyle} type="text"
                                placeholder="e.g. First Amendment, Article I Section 8, 14th Amendment Equal Protection Clause..."
                                value={form.specificProvision}
                                onChange={e => set('specificProvision', e.target.value)} />
                        </div>

                        {/* Context */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Additional context (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="Any relevant details about your specific situation, state, or what prompted this question..."
                                value={form.context}
                                onChange={e => set('context', e.target.value)} />
                        </div>

                        <button style={{ ...btnPrimary, opacity: !canProceed ? 0.5 : 1 }}
                            disabled={!canProceed}
                            onClick={() => setStep(2)}>
                            Review & generate →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Review ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Review your research request
                        </div>

                        {/* Summary */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px', fontFamily: 'sans-serif' }}>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Constitution:</div>
                                <div style={{ color: '#1e2a3a' }}>
                                    {form.scope === 'federal' ? 'Federal Constitution'
                                        : form.scope === 'state' ? `${form.state} State Constitution`
                                        : `Federal + ${form.state} State`}
                                </div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Research type:</div>
                                <div style={{ color: '#1e2a3a' }}>{selectedType?.label}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Question:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.question}</div>
                                {form.specificProvision && <>
                                    <div style={{ color: '#8a8070', fontWeight: 600 }}>Provision:</div>
                                    <div style={{ color: '#1e2a3a' }}>{form.specificProvision}</div>
                                </>}
                                {form.context && <>
                                    <div style={{ color: '#8a8070', fontWeight: 600 }}>Context:</div>
                                    <div style={{ color: '#1e2a3a' }}>{form.context}</div>
                                </>}
                            </div>
                        </div>

                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                            Review the research request above. If anything needs changing, click the ① in the progress bar to go back.
                            When ready, click Generate to produce your constitutional research report.
                        </div>

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}
                                disabled={loading} onClick={generate}>
                                {loading ? 'Researching...' : 'Generate Research Report →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Researching constitutional provisions, case law, and historical context...
                                <br />This may take 20-30 seconds for a thorough analysis.
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Research complete
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px', fontStyle: 'italic' }}>
                                    "{result.question}"
                                </div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Report
                            </button>
                        </div>

                        {/* Research display */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.research.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) {
                                    return (
                                        <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>
                                            {line.replace('## ', '')}
                                        </div>
                                    );
                                }
                                if (line.startsWith('- ') || line.startsWith('• ')) {
                                    return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                }
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Report</button>
                            <button onClick={reset} style={btnSecondary}>New research question</button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This research is generated by AI for educational and civic engagement purposes. It is not legal advice.
                            Constitutional law is complex and fact-specific. For matters with significant legal consequences,
                            consult a qualified constitutional attorney.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default ConstitutionalResearchPage;
